import { API_PALLAS_APPS_URL, WHATSAPP_CHAT_URL } from "../ducks/consts";
import toPriceDisplay from "../utils/toPriceDisplay";
import { setSubmitError } from "../ducks/checkoutSlice";
import { fetchAppEssentials } from "../ducks/appStateSlice";
import formatDateTime from "../utils/formatDateTime";
import timeoutError from "../utils/timeoutError";
import timeout from "../utils/timeout";
import { logPedido } from "./pedido-logger";

// Retry idempotente: a API deduplica pelo uuid do pedido e, em duplicado,
// retorna o pedido original com 2xx. Como submittingData (e seu uuid) é fixo
// entre tentativas, re-tentar falhas transitórias é seguro.
const MAX_ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 20000; // /pedido pode processar pagamento; tunável
const RETRY_BASE_DELAY_MS = 800; // 800ms, 1600ms, ... + jitter

const backoffDelay = (attempt) =>
  RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) + Math.floor(Math.random() * 300);

const CheckoutSubmission = {
  sendPallas: async (submittingData, dispatch) => {
    let response;
    let json;
    let attempts = 0;
    let thrown = null;
    const startedAt = Date.now();

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        attempts = attempt;
        try {
          response = await timeoutError(
            ATTEMPT_TIMEOUT_MS,
            fetch(`${API_PALLAS_APPS_URL}/pedido`, {
              method: "post",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
              body: JSON.stringify(submittingData), // mesmo uuid em toda tentativa
            }),
          );
        } catch (err) {
          // rede ou timeout: transiente. Seguro re-tentar (API deduplica por uuid).
          if (attempt < MAX_ATTEMPTS) {
            await timeout(backoffDelay(attempt));
            continue;
          }
          throw err;
        }

        // 5xx/gateway: transiente, re-tentar
        if (response.status >= 500 && attempt < MAX_ATTEMPTS) {
          await timeout(backoffDelay(attempt));
          continue;
        }

        break; // resposta final: 2xx, 4xx, ou 5xx na última tentativa
      }

      // parse defensivo: corpo de erro pode não ser JSON (corpo vazio, página de
      // erro HTML de gateway/proxy, resposta truncada). Sem isto, um throw aqui
      // pularia o tratamento de status abaixo e deixaria isSubmitting travado.
      json = await response.json().catch(() => ({}));
      if (response.status === 422) {
        dispatch(
          fetchAppEssentials(
            submittingData.shopUri,
            submittingData.isInLoco,
            false,
          ),
        );
        dispatch(setSubmitError(json.error));
        throw new ServerMessageError(json.error || {});
      } else if (response.status === 424) {
        dispatch(setSubmitError(json.error));
        throw new ServerMessageError(json.error || {});
      }

      if (!response.ok) {
        throw new Error("No 2XX response, left unhandled.");
      }

      return json;
    } catch (err) {
      thrown = err;
      throw err;
    } finally {
      // Registra todo desfecho — 2xx, 422/424, não-2xx e falha de rede — para a
      // suíte de testes da API. Inerte fora do domínio do beta.
      logPedido({
        attempts,
        durationMs: Date.now() - startedAt,
        request: submittingData,
        status: response ? response.status : null,
        response: json,
        error: thrown ? { name: thrown.name, message: thrown.message } : null,
      });
    }
  },
  sendWhatsApp: (tel, cache) => {
    const costs = cache.costs;
    const checkout = cache.checkout;
    const messageLines = [
      checkout.toDelivery
        ? `*Pedido ${cache.shortReference} Delivery*`
        : `*Pedido ${cache.shortReference} Retirar no balcão*`,
      `Data: ${formatDateTime(cache.createdAt)}`,
      "",
      ...cache.orderedProducts.reduce(
        (ps, p) => [
          ...ps,
          `*${p.name}* ${toPriceDisplay(p.price, ",")}`,
          ...p.detailsList,
          "",
        ],
        [],
      ),
      ...(checkout.showSubtotal
        ? [`Subtotal: ${toPriceDisplay(costs.subtotal, ",")}`]
        : []),
      ...(checkout.toDelivery
        ? [`Entrega: ${toPriceDisplay(costs.delivery, ",")}`]
        : []),
      ...(costs.hasValidCoupon
        ? [`Desconto: ${toPriceDisplay(costs.discount, ",")}`]
        : []),
      ...(costs.hasTakeoutDiscount
        ? [
            `Desconto para retirar: ${toPriceDisplay(
              costs.takeoutDiscount,
              ",",
            )}`,
          ]
        : []),
      `*Total:* ${toPriceDisplay(costs.total, ",")}`,
      "",
      "*Forma de pagamento*",
      costs.paymentIsCash
        ? `${checkout.payment}: ${toPriceDisplay(costs.paymentValue)}`
        : checkout.payment,
      costs.paymentIsCash && costs.paymentDiff > 0
        ? `Troco: ${toPriceDisplay(costs.paymentDiff)}`
        : "Sem troco",
      "",
      "*Cliente:*",
      checkout.name,
      `Telefone: ${checkout.telephone}`,
      ...(checkout.identification ? [`CPF: ${checkout.identification}`] : []),
      ...(checkout.toDelivery
        ? [
            "",
            "*Endereço de entrega:*",
            `${checkout.deliveryAddress.addressStreet}, ${checkout.deliveryAddress.addressNumber}`,
            checkout.deliveryAddress.complement,
            `${checkout.deliveryAddress.neighborhood} - ${checkout.deliveryAddress.city}/${checkout.deliveryAddress.state}`,
            `CEP ${checkout.deliveryAddress.postalCode}`,
          ]
        : []),
      // "",
      // `Para acompanhar o status do pedido acesse: ${window.location.origin}/${uri}/status/${cache.shortReference}`
    ];
    const message = messageLines.join("\n");
    const strippedTel = "55" + tel.toString().replace(rNonNumbers, "");
    return `${WHATSAPP_CHAT_URL}/${strippedTel}?text=${encodeWhatsAppText(message)}`;
  },
};

export default CheckoutSubmission;

class ServerMessageError extends Error {
  constructor(error) {
    super(error.message);
    this.details = error.details;
    this.isUserReadable = true;
  }
}

const rNonNumbers = /\D/g;

const WHATSAPP_TEXT_LIMIT = 4096;

/**
 * Codifica a mensagem para o `?text=` do wa.me, cortando com "..." quando
 * passa do limite.
 *
 * O corte é no texto, e não no texto já codificado: cortar no meio de um
 * "%C3%A7" gera um link que não abre. E é `encodeURIComponent`, não
 * `encodeURI`: este deixa passar `&`, `#` e `+`, e um "X-Tudo & Cia" no nome
 * do produto cortava a mensagem ali.
 */
export function encodeWhatsAppText(message) {
  let encoded = encodeURIComponent(message);
  if (encoded.length <= WHATSAPP_TEXT_LIMIT) return encoded;

  // por code point, para não partir um emoji ao meio
  const chars = Array.from(message);
  let end = chars.length;
  while (encoded.length > WHATSAPP_TEXT_LIMIT && end > 0) {
    // um caractere vira no máximo 12 ao codificar (emoji: 4 bytes × "%XX")
    const excess = encoded.length - WHATSAPP_TEXT_LIMIT;
    end = Math.max(0, end - Math.max(1, Math.floor(excess / 12)));
    encoded = encodeURIComponent(chars.slice(0, end).join("") + "...");
  }
  return encoded;
}
